import { Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useCompanies } from '../../features/company/CompanyContext';
import { Card, EmptyState, Loading, ModuleHero, Notice, StatusPill } from '../../components/ui';
import NoCompany from '../../components/NoCompany';
import RatingReportView, { RatingPdfButton } from '../../components/RatingReportView';
import RatingFeePage from '../../components/RatingFeePage';
import ProgressTracker from '../../components/ProgressTracker';
import { REPORT_STATUS, formatDate, gradeTone } from '../../lib/format';
import type { GlossaryTerm, JourneyProgress, Payment, RatingReport, StatusPanel } from '../../types/workspace';

type ReportsResponse = {
  companyStatus: string;
  locked: boolean;
  fee: Payment | null;
  publishedCount: number;
  reports: (RatingReport & { _count: { remediations: number } })[];
  latestSharedWith: number;
  isDemo: boolean;
  progress: JourneyProgress;
  glossary: GlossaryTerm[];
  statusPanel?: StatusPanel;
};

const WAITING: Record<string, string> = {
  INVITED: 'Complete your company profile and documents to get rated.',
  INTAKE: 'Upload and submit your document pack — your Micus analyst then prepares your credit rating.',
  SUBMITTED: 'Your documents are submitted. Your Micus analyst is reviewing them and preparing your rating.',
  REANALYSIS: 'Your analyst is preparing a refreshed rating from your updated documents.',
};

export default function Rating() {
  const { data, loading, error, code, reload } = useApi<ReportsResponse>('/customer/reports');
  const { refresh } = useCompanies();

  if (loading) return <Loading />;
  if (code === 'NO_COMPANY') return <NoCompany />;
  if (error || !data) return <Notice tone="rose">{error}</Notice>;

  const [latest, ...older] = data.reports;
  const onPaid = () => {
    void reload(true);
    void refresh();
  };

  return (
    <div>
      <ModuleHero
        eyebrow="Step 3 · Rated"
        title={data.locked ? 'Unlock your credit rating' : 'Your credit rating'}
        description={
          data.locked
            ? 'Complete the rating fee to open your published result. After that you can replace documents and submit again for review.'
            : 'Prepared by your Micus analyst from your verified documents. Replace files and submit for review any time you need a refreshed rating.'
        }
        actions={
          <>
            {latest && <StatusPill label={REPORT_STATUS[latest.status]?.label || latest.status} tone={REPORT_STATUS[latest.status]?.tone || 'slate'} />}
            {data.companyStatus === 'REANALYSIS' && <StatusPill label="Re-analysis in progress" tone="blue" pulse />}
            {data.locked && <StatusPill label="Payment required" tone="amber" />}
            {latest && !data.locked && <RatingPdfButton reportId={latest.id} label={`Download PDF (v${latest.version})`} />}
          </>
        }
      />
      <ProgressTracker progress={data.progress} />

      {data.statusPanel && (
        <Card className="mt-5" title={data.statusPanel.partnerReady ? 'Partner-ready' : data.statusPanel.reportPublished ? 'Published privately' : 'Assessment in progress'}>
          <p className="text-sm font-bold text-[#6b0030]">{data.statusPanel.nextAction}</p>
          <p className="mt-2 text-xs leading-relaxed text-[#9a6b82]">{data.statusPanel.publishedMeaning}</p>
          <p className="mt-1 text-xs leading-relaxed text-[#9a6b82]"><strong>Who can view:</strong> {data.statusPanel.whoCanView}</p>
        </Card>
      )}

      {data.locked ? (
        <RatingFeePage
          fee={data.fee}
          published={data.publishedCount > 0}
          waitingMessage={data.publishedCount === 0 ? WAITING[data.companyStatus] || 'Your rating appears here once your analyst publishes it.' : undefined}
          onPaid={onPaid}
        />
      ) : !latest ? (
        <div className="mt-6">
          <EmptyState
            title="No rating yet"
            body={WAITING[data.companyStatus] || 'Your rating will appear here once your analyst publishes it.'}
            cta={['INVITED', 'INTAKE'].includes(data.companyStatus) ? { label: 'Go to documents', to: '/documents' } : undefined}
          />
        </div>
      ) : (
        <div className="mt-6 grid gap-5 lg:grid-cols-[1.45fr_0.55fr]">
          <div className="space-y-4">
            <RatingReportView report={latest} defaultOpen nextAction={data.statusPanel?.nextAction || data.progress.nextAction} />
            {['RATED', 'REMEDIATING'].includes(data.companyStatus) && (
              <Notice tone="pink">
                Need a stronger file? Replace documents and{' '}
                <Link to="/documents" className="font-bold underline">
                  submit for review again
                </Link>
                .
              </Notice>
            )}
            {data.companyStatus === 'REANALYSIS' && (
              <Notice tone="pink">Your updated documents are with your analyst. A refreshed rating will appear here when published.</Notice>
            )}
            {!['REANALYSIS', 'REMEDIATING'].includes(data.companyStatus) && (
              <Notice tone="green">
                Happy with this rating?{' '}
                <Link to="/ready" className="font-bold underline">
                  Continue to Ready
                </Link>
              </Notice>
            )}
            {data.latestSharedWith > 0 && (
              <Notice tone="green">
                Shared with {data.latestSharedWith} funding partner{data.latestSharedWith > 1 ? 's' : ''}. Your Micus analyst manages partner conversations in Consult.{' '}
                <Link to="/ready" className="font-bold underline">
                  Open Ready pack
                </Link>
              </Notice>
            )}
          </div>
          <Card title="Version history" className="lg:sticky lg:top-24 lg:self-start">
            <ul className="space-y-2">
              {data.reports.map((r) => (
                <li
                  key={r.id}
                  className="flex items-center justify-between gap-2 rounded-xl px-3 py-2"
                  style={{ background: r.id === latest.id ? '#fdf2f8' : '#fff', border: '1px solid #fce7f3' }}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-[#6b0030]">v{r.version}</p>
                    <p className="truncate text-xs text-[#c084a0]">{formatDate(r.publishedAt || r.createdAt)}</p>
                  </div>
                  <StatusPill label={`${r.grade} · ${r.score}`} tone={gradeTone(r.score)} />
                </li>
              ))}
            </ul>
          </Card>
          {older.length > 0 && (
            <div className="space-y-4 lg:col-span-1">
              <p className="text-xs font-bold uppercase tracking-wider text-[#c084a0]">Previous versions</p>
              {older.map((r) => (
                <RatingReportView key={r.id} report={r} />
              ))}
            </div>
          )}
        </div>
      )}
      {/* With a visible report the sticky bar inside RatingReportView carries the next action; avoid two bars. */}
      {data.statusPanel?.nextAction && (!latest || data.locked) && (
        <div className="fixed inset-x-3 bottom-3 z-30 rounded-xl bg-[#6b0030] px-4 py-3 text-center text-xs font-bold text-white shadow-xl sm:hidden">
          Next action: {data.statusPanel.nextAction}
        </div>
      )}
    </div>
  );
}
